import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { BlogTableOfContents } from "@/components/BlogTableOfContents";
import { BlogReadingProgress } from "@/components/BlogReadingProgress";
import { Link } from "react-router-dom";
import { Calendar, Clock, ArrowLeft, Brain, Users, Eye, Heart, Play, Sparkles } from "lucide-react";
import heroImage from "@/assets/blog/study-with-me-science.jpg";
import stopProcrastinationImage from "@/assets/blog/stop-procrastination-hero.jpg";
import onlineLearningImage from "@/assets/blog/online-learning-community.jpg";

const tocItems = [
  { id: "how-it-works", title: "How the Study With Me Videos Work" },
  { id: "psychology", title: "The Psychology Behind Study With Me" },
  { id: "study-videos", title: "Popular Study With Me Videos to Try" },
  { id: "conclusion", title: "Conclusion" },
];

const studyWithMeVideos = [
  { id: "arTIUUGVx34", title: "2-HOUR STUDY WITH ME | Pomodoro 50/10" },
  { id: "lPDgej2Xn9s", title: "4-HOUR STUDY WITH ME | Calm Piano Music" },
  { id: "GS7J3LiNU0g", title: "STUDY WITH ME in Real Time | 2 Hours" },
  { id: "VNu9EF6d9JA", title: "Study With Me LIVE | Pomodoro Timer" },
  { id: "McWH0UoR5dg", title: "3 HOUR STUDY WITH ME | Background Noise" },
  { id: "1lBGO_dJWkQ", title: "Study With Me 2 HOURS | No Music" },
];

const ScienceBehindStudyWithMe = () => {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO 
        title="The Science Behind the Study With Me Trend"
        description="Discover the psychology and science behind why Study With Me videos are so effective. Learn about social facilitation, accountability, and how this trend helps millions stay focused."
        keywords="study with me, gongbang, study with me videos, social facilitation, study psychology, online study rooms, CSW, productivity science"
        canonicalUrl="/blog/science-behind-study-with-me/"
      />
      <Header />
      <BlogReadingProgress />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-12 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-background to-background" />
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-secondary/10 rounded-full blur-3xl" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto">
            <Link 
              to="/blog/" 
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-10 font-body"
              onClick={() => window.scrollTo(0, 0)}
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>
            
            <div className="flex flex-wrap items-center gap-4 mb-8">
              <span className="px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium font-body">
                Productivity
              </span>
              <div className="flex items-center gap-2 text-muted-foreground text-sm font-body">
                <Calendar className="w-4 h-4" />
                <span>December 16, 2024</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground text-sm font-body">
                <Clock className="w-4 h-4" />
                <span>8 min read</span>
              </div>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold mb-10 leading-[1.1] tracking-tight">
              The Science Behind the "Study With Me" Trend
            </h1>

            {/* Featured Image */}
            <img 
              src={heroImage} 
              alt="Cozy study setup with laptop showing a study with me video" 
              className="w-full aspect-video object-cover rounded-2xl mb-8"
            />
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="pb-24 relative">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Main Content */}
              {/* Lead Paragraph */}
              <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed font-body mb-10">
                The "Study With Me" trend has taken the internet by storm. If you've scrolled through YouTube or TikTok, then you've probably stumbled upon countless videos of people quietly studying—sometimes for hours—with nothing but just the sound of their pens or the clicking of their keyboards.
              </p>

              <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                It's easy to think at first that it was odd. I mean, why would anyone want to watch someone else study? But behind this seemingly simple trend, I noticed that there was actually science behind it. It turns out this type of trend actually helps people stay motivated and focused.
              </p>

              <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                But where does this come from? Just like how South Korea's culture and music has taken the world by storm, the "Gongbang" culture was also one of these. Basically, people broadcast themselves studying live and called it "Study With Me" videos. Over time, it has evolved into a global trend that offers more than just background noise.
              </p>

              <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
                YouTubers and vloggers who film "Study With Me" were able to foster a sense of shared purpose, accountability, and support—even when it's thousands of miles away. So, let's dive into the science behind why the "Study With Me" trend works so effectively, exploring how it taps into our natural social behaviors and emotions to make studying less lonely and more productive.
              </p>

              {/* Mobile TOC */}
              <div className="lg:hidden mb-12">
                <BlogTableOfContents items={tocItems} />
              </div>

              {/* Section 1: How it Works */}
              <div id="how-it-works" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10">
                <div className="flex items-start gap-6">
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Play className="w-7 h-7 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                      How the "Study With Me" Videos Work
                    </h2>
                  </div>
                </div>
              </div>

              <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                So how exactly does the "Study With Me" video work? "Study With Me" videos are basically designed to recreate the feeling of studying alongside someone else. They do this through a few simple but effective features.
              </p>

              <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                First, videos like these typically show a real person—oftentimes a streamer or YouTuber—often with a visible timer or Pomodoro technique. This makes it easier for viewers to pace themselves and stay on track without constantly checking the clock.
              </p>

              <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                Aside from that, these videos also include ambient sounds like page turning, pen scratching, keyboard typing, or even just soft background music. Doing this creates a calming and distraction-free atmosphere that mimics a cozy study space. The minimal interaction style—no talking or interruptions—helps maintain a focused environment, much like sitting quietly in a library or coffee shop.
              </p>

              <div className="bg-muted/50 rounded-xl p-6 mb-10">
                <h3 className="font-display font-bold text-lg mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-primary" />
                  Key Elements of Study With Me Videos
                </h3>
                <ul className="space-y-2 text-foreground/90 font-body list-disc list-inside">
                  <li>Real person studying with visible timer or Pomodoro technique</li>
                  <li>Ambient sounds: page turning, pen scratching, keyboard typing</li>
                  <li>Minimal interaction style with no talking or interruptions</li>
                  <li>Calming atmosphere that mimics a cozy study space</li>
                  <li>Options for prerecorded videos or live streams</li>
                </ul>
              </div>

              <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                There are also different formats to choose from: some people prefer prerecorded videos they can pause and rewind, while others find live streams with real-time progress more motivating because of the sense of real-time companionship. Read about <Link to="/blog/how-turning-on-my-camera-boosted-my-productivity/" className="text-primary hover:underline" onClick={() => window.scrollTo(0, 0)}>how turning on your camera boosts productivity</Link>.
              </p>

              <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
                Platforms like <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">CSW</a> take this a step further by offering virtual study rooms where you can join others in a structured session, complete with timers, a to-do list, chat features, and even a section where you can play YouTube videos if you're taking a break. Try our <Link to="/study-with-me/" className="text-primary hover:underline" onClick={() => window.scrollTo(0, 0)}>Study With Me</Link> feature or explore <Link to="/study-stream/" className="text-primary hover:underline" onClick={() => window.scrollTo(0, 0)}>Study Stream</Link> for live sessions.
              </p>

              {/* Section 2: Psychology */}
              <div id="psychology" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10">
                <div className="flex items-start gap-6">
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Brain className="w-7 h-7 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                      The Psychology Behind Study With Me
                    </h2>
                  </div>
                </div>
              </div>

              <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                The psychology behind the "Study With Me" trend is actually pretty fascinating. The trend is deeply rooted in our natural social behaviors and cognitive processes. Have you ever noticed how you tend to focus better when someone's around, even if they're just quietly working themselves?
              </p>

              <div className="bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20 rounded-2xl p-6 mb-10">
                <div className="flex items-start gap-4">
                  <Eye className="w-8 h-8 text-primary flex-shrink-0" />
                  <div>
                    <h3 className="font-display font-bold text-lg mb-2">Social Facilitation Effect</h3>
                    <p className="text-muted-foreground font-body">
                      This is because of social facilitation—where you basically tend to perform better on tasks when you know somebody is observing you. Even though you're watching someone study through a screen, just knowing they're there "with you" creates a gentle kind of accountability that keeps distractions at bay.
                    </p>
                  </div>
                </div>
              </div>

              <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                Another big factor is the feeling of not being alone. Studying can get pretty lonely, and "Study With Me" videos give that sense of connection. The more you do it, the more you feel like you're grinding towards the same goal, even if you don't directly interact. Study With Me videos then make you feel like you're having a digital study buddy who's quietly rooting for you.
              </p>

              <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                Watching these types of videos can create a shared experience and overall ease anxiety and make studying feel way less overwhelming. Aside from that, viewers also actively engage with Study With Me content, either by syncing their study sessions with the video or participating in live chats or even just streaming it together with your friends in a 24/7 online study room.
              </p>

              <div className="space-y-6 mb-12">
                <div className="flex gap-6">
                  <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-display font-bold text-xl flex-shrink-0">
                    1
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-xl mb-3">Accountability Drivers</h3>
                    <p className="text-lg leading-relaxed font-body text-muted-foreground">
                      The visible presence of others studying creates a gentle form of social pressure that helps you stay on task and resist distractions.
                    </p>
                  </div>
                </div>

                <div className="flex gap-6">
                  <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-display font-bold text-xl flex-shrink-0">
                    2
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-xl mb-3">Community Building</h3>
                    <p className="text-lg leading-relaxed font-body text-muted-foreground">
                      Even without direct interaction, watching others study creates a sense of belonging and shared purpose that reduces feelings of isolation.
                    </p>
                  </div>
                </div>

                <div className="flex gap-6">
                  <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-display font-bold text-xl flex-shrink-0">
                    3
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-xl mb-3">Consistent Habits</h3>
                    <p className="text-lg leading-relaxed font-body text-muted-foreground">
                      The routine of joining study sessions harnesses principles like positive reinforcement and habit formation, making productivity more sustainable.
                    </p>
                  </div>
                </div>
              </div>

              <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
                The same psychology actually works when it comes to 24/7 online study rooms. Unlike Study With Me videos which eventually end, online study rooms are always open anytime or anywhere in the world. Having the same benefits as a Study With Me video, online study rooms were easier for me to access. It also never became monotonous because unlike Study With Me videos, you generally have to wait for the YouTuber or streamer to upload another video. Learn more about <Link to="/blog/benefits-of-online-learning-community/" className="text-primary hover:underline" onClick={() => window.scrollTo(0, 0)}>the benefits of online learning communities</Link> and why they're so effective for student success.
              </p>

              {/* Section 3: Study Videos */}
              <div id="study-videos" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10">
                <div className="flex items-start gap-6">
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Users className="w-7 h-7 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                      Popular Study With Me Videos to Try
                    </h2>
                  </div>
                </div>
              </div>

              <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                Ready to experience the benefits of Study With Me for yourself? Here are some popular videos you can try right now. Each one offers a different vibe—from calming piano music to silent focus sessions with just ambient sounds.
              </p>

              {/* YouTube Video Embeds */}
              <div className="space-y-8 mb-12">
                {studyWithMeVideos.map((video, index) => (
                  <div key={video.id} className="rounded-2xl overflow-hidden border border-border">
                    <div className="aspect-video">
                      <iframe
                        src={`https://www.youtube.com/embed/${video.id}`}
                        title={video.title}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="w-full h-full"
                      />
                    </div>
                    <div className="p-4 bg-card">
                      <p className="font-display font-semibold text-lg">{video.title}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-muted/50 rounded-xl p-6 mb-12">
                <h3 className="font-display font-bold text-lg mb-4">Pro Tip: Try CSW for Real-Time Study Sessions</h3>
                <p className="text-foreground/90 font-body mb-4">
                  While YouTube videos are great, they're not interactive. For a more immersive experience, try <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">CSW's 24/7 online study rooms</a> where you can study with real people in real-time, complete with Pomodoro timers, task management, and a supportive community.
                </p>
              </div>

              {/* Conclusion */}
              <h2 id="conclusion" className="text-3xl md:text-4xl font-display font-bold mt-16 mb-8 tracking-tight">
                Conclusion
              </h2>

              <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                Overall, the "Study With Me" trend isn't just a trend. It's a way for us to turn our solo study sessions into a shared experience that helps boost productivity and focus but also keeps loneliness at bay. Having calming background sounds, structured Pomodoro timers, or even virtual study rooms offered by CSW, these types of technologies help us get accessible tools to make studying less daunting and more engaging.
              </p>

              <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
                Honestly, this is the type of trend that makes technology much more worth it. Whether you're watching a prerecorded video, joining a live stream, or hopping into a 24/7 online study room, the science is clear: studying together—even virtually—is more effective than studying alone.
              </p>

              {/* CTA Section */}
              <div className="bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20 rounded-2xl p-8 md:p-12 text-center">
                <Heart className="w-12 h-12 text-primary mx-auto mb-6" />
                <h3 className="text-2xl md:text-3xl font-display font-bold mb-4">
                  Ready to Study With Others?
                </h3>
                <p className="text-lg text-muted-foreground font-body mb-8 max-w-xl mx-auto">
                  Join thousands of students in CSW's free 24/7 online study rooms. Experience the science of social facilitation and never study alone again.
                </p>
                <a 
                  href="https://app.csw.live" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-8 py-4 bg-primary text-primary-foreground rounded-xl font-semibold text-lg hover:bg-primary/90 transition-colors"
                >
                  Join CSW for Free
                </a>
              </div>

              {/* Related Articles Section */}
              <div className="mt-20">
                <h2 className="text-3xl md:text-4xl font-display font-bold mb-10 tracking-tight">
                  Related Articles
                </h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <Link 
                    to="/blog/how-to-stop-procrastination/"
                    onClick={() => window.scrollTo(0, 0)}
                    className="group bg-card border border-border rounded-2xl overflow-hidden hover:border-primary/30 transition-all"
                  >
                    <img 
                      src={stopProcrastinationImage} 
                      alt="How to stop procrastination" 
                      className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="p-5">
                      <span className="text-sm text-primary font-medium font-body mb-2 block">Productivity</span>
                      <h3 className="text-lg font-display font-bold group-hover:text-primary transition-colors">
                        How to Stop Procrastination
                      </h3>
                    </div>
                  </Link>

                  <Link 
                    to="/blog/benefits-of-online-learning-community/"
                    onClick={() => window.scrollTo(0, 0)}
                    className="group bg-card border border-border rounded-2xl overflow-hidden hover:border-primary/30 transition-all"
                  >
                    <img 
                      src={onlineLearningImage} 
                      alt="Benefits of online learning community" 
                      className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="p-5">
                      <span className="text-sm text-primary font-medium font-body mb-2 block">Online Learning</span>
                      <h3 className="text-lg font-display font-bold group-hover:text-primary transition-colors">
                        The 5 Benefits of Having an Online Learning Community
                      </h3>
                    </div>
                  </Link>
                </div>
              </div>

          </div>
          
          {/* Floating Sticky TOC - Desktop only */}
          <aside className="hidden lg:block fixed right-8 xl:right-16 top-24 z-40">
            <BlogTableOfContents items={tocItems} collapsible />
          </aside>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default ScienceBehindStudyWithMe;
